import React, { useEffect, useState } from 'react';
import { CFTokens } from '@connectrn/ds-ui-base';
import { BreakpointProviderContext } from './BreakpointProviderContext';
//
// Breakpoint provider allows for making adjustments to React content
// based on changes in viewports.
// It is dependent upon `matchMedia`. For browser support see https://caniuse.com/matchmedia
//
// Provide a set of css media queries in an object.
// The keys of that object can then bet tested as booleans within the returned value from `useCFBreakpoint()`
// in order to determine if a particular query matches the current viewport.
//
export const CFBreakpointProvider = ({ children, breakpointQueries = CFTokens.WEB_BREAKPOINT_QUERIES, }) => {
    // Store the current set of matching breakpoints
    const [breakpoint, setBreakpoint] = useState({});
    // Watch for changes to provided queries object
    // and ensures the matchQuery API is configured to use them
    useEffect(() => {
        const mediaQueryList = {};
        const keys = Object.keys(breakpointQueries);
        let isAttached = false;
        // Use matchMedia API to check each provided breakpoint, "media" for matches
        // As a result the `breakpoint` is updated
        const handleQueryListener = () => {
            const updateMatches = keys.reduce((acc, media) => {
                acc[media] = Boolean(mediaQueryList[media] && mediaQueryList[media].matches);
                return acc;
            }, {});
            setBreakpoint(updateMatches);
        };
        if (window && window.matchMedia) {
            // Attach matchMedia API handles to each breakpoint query
            // These are stored in `mediaQueryList`
            const matches = {};
            keys.forEach((media) => {
                if (typeof breakpointQueries[media] === 'string') {
                    mediaQueryList[media] = window.matchMedia(breakpointQueries[media]);
                    matches[media] = mediaQueryList[media].matches;
                }
                else {
                    matches[media] = false;
                }
            });
            // Sets in itial set of matches
            setBreakpoint(matches);
            // A boolean to check later before detatching listeners
            isAttached = true;
            // Attach listeners to each breakpoint media query
            keys.forEach((media) => {
                if (typeof breakpointQueries[media] === 'string') {
                    mediaQueryList[media].addListener(handleQueryListener);
                }
            });
        }
        // Detatch listeners when appropriate
        return () => {
            if (isAttached) {
                keys.forEach((media) => {
                    if (typeof breakpointQueries[media] === 'string') {
                        mediaQueryList[media].removeListener(handleQueryListener);
                    }
                });
            }
        };
    }, [breakpointQueries]);
    return (React.createElement(BreakpointProviderContext.Provider, { value: { breakpoint: breakpoint } }, children));
};
